import React, { useState, useRef, useEffect } from 'react';
import { MemberModel, UserRole, BranchModel } from '../../../../models';
import { ExternalLink, Edit2, Trash2, User, School, MapPin, Phone, Link as LinkIcon, Mail, MoreVertical } from 'lucide-react';

interface MemberCardProps {
    member: MemberModel;
    branches: BranchModel[];
    isSelected: boolean;
    onSelect: (member: MemberModel) => void;
    onUpdate?: (member: MemberModel) => void;
    onDelete?: (memberId: string) => void;
    onShare?: (member: MemberModel) => void;
    isOnline?: boolean;
}

const ROLE_LABELS: Record<UserRole, string> = {
    super_admin: '최고 관리자',
    manager: '매니저',
    staff: '직원',
    teacher: '선생님',
    student: '학생',
    parent: '학부모',
};

const ROLE_COLORS: Record<UserRole, string> = {
    super_admin: 'bg-rose-500/10 text-rose-600 ring-rose-500/20',
    manager: 'bg-indigo-500/10 text-indigo-600 ring-indigo-500/20',
    staff: 'bg-indigo-500/10 text-indigo-650 ring-indigo-500/20',
    teacher: 'bg-indigo-500/10 text-indigo-600 ring-indigo-500/20',
    student: 'bg-emerald-500/10 text-emerald-600 ring-emerald-500/20',
    parent: 'bg-amber-500/10 text-amber-600 ring-amber-500/20',
};

const MemberCard: React.FC<MemberCardProps> = ({
    member,
    branches,
    isSelected,
    onSelect,
    onUpdate,
    onDelete,
    onShare,
    isOnline,
}) => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const menuRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                setIsMenuOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        if (isOnline) {
            console.log(`[Component] Card for ${member.name} (ID: ${member.id}) IS ONLINE!`);
        }
    }, [isOnline, member.name, member.id]);

    const getBranchName = (branchId?: string) => {
        if (!branchId) return '-';
        const branch = branches.find(b => b.id === branchId);
        return branch?.name || '-';
    };

    const handleMemberClick = () => {
        const url = `/student-view/${member.id}`;
        window.open(url, '_blank');
        onSelect(member);
    };

    return (
        <div
            className={`bg-white rounded-2xl border transition-all hover:shadow-md group w-full ${isSelected
                ? 'border-indigo-500 ring-4 ring-indigo-500/10 shadow-lg z-10 relative'
                : 'border-slate-200 hover:border-indigo-200'
                }`}
        >
            <div className="p-5 flex items-center gap-6">
                {/* Profile Icon */}
                <div className="flex-shrink-0">
                    <div className={`relative w-14 h-14 rounded-2xl flex items-center justify-center text-xl font-black shadow-inner ${member.role === 'student' ? 'bg-indigo-50 text-indigo-600' :
                        'bg-slate-100 text-slate-500'
                        }`}>
                        {member.name[0]}
                        {isOnline && (
                            <span className="absolute -top-1 -right-1 flex h-4 w-4 z-[100]">
                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                                <span className="relative inline-flex rounded-full h-4 w-4 bg-emerald-500 border-2 border-white shadow-[0_0_8px_rgba(16,185,129,0.5)]"></span>
                            </span>
                        )}
                    </div>
                </div>

                {/* Main Info */}
                <div className="flex-1 min-w-0 grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-6 items-center">
                    <div className="md:col-span-4">
                        <div className="flex items-center gap-2 mb-1">
                            <h3 className="text-lg font-black text-slate-800 truncate">{member.name}</h3>
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-black ring-1 ring-inset ${ROLE_COLORS[member.role]}`}>
                                {ROLE_LABELS[member.role]}
                            </span>
                            {member.enrollmentStatus && (
                                <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-black ring-1 ring-inset ml-1 ${member.enrollmentStatus === '재원' ? 'bg-green-500/10 text-green-600 ring-green-500/20' :
                                    member.enrollmentStatus === '휴원' ? 'bg-orange-500/10 text-orange-600 ring-orange-500/20' :
                                        member.enrollmentStatus === '퇴원' ? 'bg-slate-500/10 text-slate-600 ring-slate-500/20' :
                                            'bg-slate-100 text-slate-600 ring-slate-500/10'
                                    }`}>
                                    {member.enrollmentStatus}
                                </span>
                            )}
                        </div>
                        <p className="text-xs font-medium text-slate-400 truncate">{member.email}</p>
                    </div>

                    <div className="md:col-span-4 space-y-1">
                        <div className="flex items-center gap-2 text-slate-600">
                            <School size={14} className="text-slate-400" />
                            <span className="text-sm font-bold truncate">
                                {member.schoolName || '-'}
                                {member.grade && <span className="text-slate-400 font-normal ml-1">({member.grade})</span>}
                            </span>
                        </div>

                    </div>

                    <div className="md:col-span-4">
                        <div className="flex items-center gap-2 text-slate-600 mb-1">
                            <Phone size={14} className="text-slate-400" />
                            <span className="text-sm font-bold tracking-tight">{member.phone || '-'}</span>
                        </div>
                        {(member.guardians?.length || member.parentPhone || member.parentEmail) && (
                            <div className="space-y-1">
                                {member.guardians && member.guardians.length > 0 ? (
                                    member.guardians.slice(0, 2).map((g, i) => (
                                        <div key={i} className="space-y-1">
                                            <div className="flex items-center gap-2 text-slate-400">
                                                <User size={14} className="text-slate-300" />
                                                <span className="text-xs font-medium tracking-tight">{g.relation}: {g.phone}</span>
                                            </div>
                                            {g.email && (
                                                <div className="flex items-center gap-2 text-slate-400">
                                                    <Mail size={14} className="text-slate-300" />
                                                    <span className="text-xs font-medium tracking-tight">{g.email}</span>
                                                </div>
                                            )}
                                        </div>
                                    ))
                                ) : (
                                    <>
                                        {member.parentPhone && (
                                            <div className="flex items-center gap-2 text-slate-400">
                                                <User size={14} className="text-slate-300" />
                                                <span className="text-xs font-medium tracking-tight">부모: {member.parentPhone}</span>
                                            </div>
                                        )}
                                        {member.parentEmail && (
                                            <div className="flex items-center gap-2 text-slate-400">
                                                <Mail size={14} className="text-slate-300" />
                                                <span className="text-xs font-medium tracking-tight">{member.parentEmail}</span>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {/* Actions */}
                <div className="flex-shrink-0 flex items-center gap-2 pl-4 border-l border-slate-100 relative">
                    <button
                        onClick={handleMemberClick}
                        className="flex flex-col items-center justify-center w-16 gap-1 group/btn"
                        title="포털 열기"
                    >
                        <div className="w-9 h-9 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center transition-all group-hover/btn:bg-indigo-600 group-hover/btn:text-white group-hover/btn:scale-110 group-hover/btn:shadow-lg">
                            <ExternalLink size={18} />
                        </div>
                        <span className="text-[10px] font-bold text-slate-400 group-hover/btn:text-indigo-600 transition-colors">포털 열기</span>
                    </button>

                    <div className="relative" ref={menuRef}>
                        <button
                            onClick={() => setIsMenuOpen(!isMenuOpen)}
                            className="p-2 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-all"
                        >
                            <MoreVertical size={20} />
                        </button>

                        {isMenuOpen && (
                            <div className="absolute right-0 top-full mt-2 w-48 bg-white rounded-xl shadow-xl border border-slate-100 py-1 z-20 animate-in fade-in zoom-in-95 duration-200">
                                {onUpdate && (
                                    <button
                                        onClick={() => { setIsMenuOpen(false); onUpdate(member); }}
                                        className="w-full text-left px-4 py-3 text-sm font-bold text-slate-700 hover:bg-slate-50 flex items-center gap-2"
                                    >
                                        <Edit2 size={16} className="text-slate-400" /> 회원 정보 수정
                                    </button>
                                )}
                                {onShare && (
                                    <button
                                        onClick={() => { setIsMenuOpen(false); onShare(member); }}
                                        className="w-full text-left px-4 py-3 text-sm font-bold text-slate-700 hover:bg-emerald-50 flex items-center gap-2"
                                    >
                                        <LinkIcon size={16} className="text-emerald-500" /> 링크 복사
                                    </button>
                                )}
                                <div className="h-[1px] bg-slate-100 my-1"></div>
                                {onDelete && (
                                    <button
                                        onClick={() => {
                                            setIsMenuOpen(false);
                                            if (confirm(`${member.name} 회원을 삭제하시겠습니까?`)) {
                                                onDelete(member.id);
                                            }
                                        }}
                                        className="w-full text-left px-4 py-3 text-sm font-bold text-rose-600 hover:bg-rose-50 flex items-center gap-2"
                                    >
                                        <Trash2 size={16} /> 회원 삭제
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MemberCard;
